import type { TLeoStatus } from "@/zapi-frontend/stores/leoChatStore";
import type { TLeoChatProgress } from "../api/leoChat";

export type { TLeoStatus };

// A template-less project's Home page travels with the chapters as `work/home.json`; the backend
// reports it applied under this slug, which no chapter has.
export const HOME_SLUG = "home";
export const OUTLINE_SLUG = "slides";

const READ_TOOLS = new Set(["Read", "LS", "NotebookRead"]);
const EDIT_TOOLS = new Set(["Edit", "Write", "MultiEdit", "NotebookEdit"]);
const SEARCH_TOOLS = new Set(["Grep", "Glob", "WebSearch", "WebFetch"]);
// The files Leo writes its reply and actions into, under out/; never a chapter.
const REPLY_FILES = new Set(["answer.md", "summary.md", "actions.json"]);
// input/task.md: the request itself, which Leo reads first.
const REQUEST_FILE = "task.md";
const CHAPTER_FILE_RE = /work\/([A-Za-z0-9._-]+?)\.(md|json)\b/;
const SEARCH_COMMAND_RE = /^\s*(rg|grep|ag|find|ls)\b/;
// A slide's page is `slide-NN.html` and the deck's look `theme.css`, written in work/ or out/; the
// sample deck Leo designs to is the same names with `.md` added.
const SLIDE_FILE_RE = /(?:^|[/\s])slide-(\d+)\.html(?![.\w])/;
const THEME_FILE_RE = /(?:^|[/\s])theme\.css(?![.\w])/;
const SAMPLE_FILE_RE = /(?:^|[/\s])(?:slide-\d+\.html|theme\.css)\.md\b/;
const WRITE_COMMAND_RE = /(^|[|;&]\s*)(sed -i|tee|python3?|node|cp|mv)\b|(?<![<>])>(?!>)|>>/;

// Which slide Leo is reading or changing, or that it is working on the deck's design.
const deckStatus = (target: string, isEdit: boolean): TLeoStatus | null => {
  const slide = SLIDE_FILE_RE.exec(target);
  if (slide) return { kind: isEdit ? "editingSlide" : "readingSlide", slide: Number(slide[1]) };
  if (THEME_FILE_RE.test(target) || SAMPLE_FILE_RE.test(target)) return { kind: "designing" };
  return null;
};

// The model often reaches for the shell instead of the Read tool. A `Bash: <command>` line
// still names the chapter file it touches, so it is read the same way: the chapter comes from
// the `work/` path, the verb decides between reading, editing and searching.
const bashStatus = (command: string, chapterTitle: (slug: string) => string | undefined): TLeoStatus => {
  const file = CHAPTER_FILE_RE.exec(command);
  if (SEARCH_COMMAND_RE.test(command)) {
    const quoted = /["']([^"']+)["']/.exec(command);
    return { kind: "searching", query: quoted?.[1] ?? (file ? chapterTitle(file[1]) ?? file[1] : "the memo") };
  }
  if (!file) return deckStatus(command, WRITE_COMMAND_RE.test(command)) ?? { kind: "thinking" };
  const slug = file[1];
  const chapter = chapterTitle(slug) ?? slug;
  return WRITE_COMMAND_RE.test(command) ? { kind: "editing", chapter, slug } : { kind: "reading", chapter, slug };
};

// The runner reports the last tool it saw as `Tool: target` (target = file name or pattern),
// `done: Tool` once the result is back, or the model's own text. The page turns that into a
// sentence a reader recognises: which chapter is being read or edited, what is searched for.
export const leoStatus = (
  progress: TLeoChatProgress | null | undefined,
  chapterTitle: (slug: string) => string | undefined
): TLeoStatus => {
  if (!progress || typeof progress.assistantTurns !== "number") return { kind: "starting" };
  // `done: Tool: target` is the same step seen after its result came back; the chapter is
  // still the one being worked on until the next tool call names another.
  const action = (progress.lastTool ?? progress.lastAction ?? "").replace(/^done: /, "");
  const match = /^([A-Za-z]+): (.+)$/.exec(action);
  if (!match) return { kind: "thinking" };
  const [, tool, target] = match;
  if (tool === "Bash") return bashStatus(target, chapterTitle);
  const deck = READ_TOOLS.has(tool) || EDIT_TOOLS.has(tool) ? deckStatus(target, EDIT_TOOLS.has(tool)) : null;
  if (deck) return deck;
  const slug = target.replace(/\.(md|json)$/, "");
  const title = chapterTitle(slug);
  const chapter = title ?? slug;
  if (EDIT_TOOLS.has(tool) && title === undefined && REPLY_FILES.has(target)) return { kind: "replying" };
  if (READ_TOOLS.has(tool) && title === undefined && target === REQUEST_FILE) return { kind: "starting" };
  if (READ_TOOLS.has(tool)) return { kind: "reading", chapter, slug };
  if (EDIT_TOOLS.has(tool)) return { kind: "editing", chapter, slug };
  if (SEARCH_TOOLS.has(tool)) return { kind: "searching", query: target };
  return { kind: "thinking" };
};

// Only a backend that does not report the edited files falls back to the live status.
export const leoEditedHome = (progress: TLeoChatProgress | null | undefined, status: TLeoStatus): boolean =>
  progress?.editedFiles
    ? progress.editedFiles.includes(`${HOME_SLUG}.json`)
    : status.kind === "editing" && status.slug === HOME_SLUG;
